// Document Pipeline Panel - Shows ingestion pipeline metrics
//
// Displays document counts by status, type, recent errors, and processing times.

import { useEffect, useState, useCallback } from 'react';
import { systemHealthService } from '../../services/systemHealth';
import type { DocumentPipelineResponse } from '../../services/systemHealth';
import { formatDate } from '../../utils/dateFormatters';
import styles from './StatisticsTab.module.css';

export default function DocumentPipelinePanel() {
  const [data, setData] = useState<DocumentPipelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await systemHealthService.getDocumentPipeline();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className={styles.loading}>Loading document pipeline...</div>;

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {data && (
        <>
          {/* KPIs */}
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Total Documents</span>
              <span className={styles.kpiValue}>
                {data.total_documents.toLocaleString()}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Avg Processing Time</span>
              <span className={styles.kpiValue}>
                {data.avg_processing_seconds != null
                  ? `${data.avg_processing_seconds}s`
                  : 'N/A'}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Error Count</span>
              <span
                className={styles.kpiValue}
                style={
                  data.recent_errors.length > 0 ? { color: '#dc2626' } : undefined
                }
              >
                {data.recent_errors.length}
              </span>
            </div>
          </div>

          {/* Status Breakdown */}
          {data.by_status.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Documents by Status</div>
              <StatusBarChart items={data.by_status} />
            </div>
          )}

          {/* Type Breakdown */}
          {data.by_type.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Documents by Type</div>
              <StatusBarChart items={data.by_type} />
            </div>
          )}

          {/* Recent Errors */}
          {data.recent_errors.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Recent Errors</div>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Status</th>
                    <th>Error Stage</th>
                    <th>Error</th>
                    <th className={styles.numericCell}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_errors.map((err) => (
                    <tr key={err.id}>
                      <td
                        style={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={err.filename ?? undefined}
                      >
                        {err.filename ?? '—'}
                      </td>
                      <td>{err.status}</td>
                      <td>{err.error_stage ?? '—'}</td>
                      <td
                        style={{
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#dc2626',
                          fontSize: '0.8125rem',
                        }}
                        title={err.error_message ?? undefined}
                      >
                        {err.error_message ?? '—'}
                      </td>
                      <td className={styles.numericCell}>
                        {err.updated_at ? formatDate(err.updated_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
            <button
              onClick={loadData}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#3b82f6',
                background: 'none',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
        </>
      )}
    </>
  );
}

function StatusBarChart({
  items,
}: {
  items: { status: string; count: number }[];
}) {
  const maxCount = Math.max(...items.map((i) => i.count), 1);

  return (
    <div className={styles.barChart}>
      {items.map((item) => (
        <div key={item.status} className={styles.barRow}>
          <span className={styles.barLabel} style={{ width: 160 }}>
            {item.status}
          </span>
          <div className={styles.barTrack}>
            <div
              className={styles.barFill}
              style={{
                width: `${(item.count / maxCount) * 100}%`,
                background: '#3b82f6',
              }}
            />
          </div>
          <span className={styles.barValue}>
            {item.count.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}
